/** Mirrors SuprBackend `src/constants/homeBanners.ts` — customer-app banner destinations. */
export const HOME_BANNER_TYPE = 'app_home_banners';

export const HOME_BANNER_MODULES = [
  { value: 'food', label: 'Food' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'transport', label: 'Transport / Taxi' },
  { value: 'on_demand', label: 'Home Services' },
  { value: 'porter', label: 'Porter' },
] as const;

export const HOME_BANNER_SCREENS = [
  { value: 'food_home', label: 'Food Home' },
  { value: 'grocery_home', label: 'Grocery Home' },
  { value: 'transport_home', label: 'Transport Home' },
  { value: 'on_demand_home', label: 'Home Services Home' },
  { value: 'porter_home', label: 'Porter Home' },
  { value: 'offers', label: 'Offers' },
  { value: 'external', label: 'External URL' },
] as const;

export const HOME_BANNER_SCREENS_BY_MODULE: Record<string, string[]> = {
  food: ['food_home', 'offers', 'external'],
  grocery: ['grocery_home', 'offers', 'external'],
  transport: ['transport_home', 'offers', 'external'],
  on_demand: ['on_demand_home', 'offers', 'external'],
  porter: ['porter_home', 'offers', 'external'],
};

export const HOME_BANNER_LIMITS = {
  name: 60,
  title: 60,
  subtitle: 100,
  ctaLabel: 20,
  badge: 12,
  description: 300,
} as const;

export const HOME_BANNER_BADGE_PATTERN = /^[A-Za-z0-9%₹$&+!.\- ]+$/;
export const HOME_BANNER_IMAGE_PATTERN = /^(https?:\/\/\S+|\/\S+)$/i;
export const HOME_BANNER_URL_PATTERN = /^https?:\/\/[^\s/$.?#].\S*$/i;

const REPEATED_CHAR = /(.)\1{3,}/u;
const REPEATED_WORD = /\b(\w+)(\s+\1\b)+/iu;

export function repetitionError(label: string, value: string): string | undefined {
  if (!value) return undefined;
  if (REPEATED_CHAR.test(value)) return `${label} must not contain the same character repeated 4 or more times`;
  if (REPEATED_WORD.test(value)) return `${label} must not repeat the same word back-to-back`;
  return undefined;
}

export function moduleLabel(value?: string) {
  return HOME_BANNER_MODULES.find((m) => m.value === value)?.label || value || '—';
}

export function screenLabel(value?: string) {
  return HOME_BANNER_SCREENS.find((s) => s.value === value)?.label || value || '—';
}

export type HomeBannerActionType = 'none' | 'url' | 'screen' | 'promo';

/** Derived tap action stored alongside `targetScreen` for apps that switch on `actionType`. */
export function bannerActionType(targetScreen?: string): HomeBannerActionType {
  if (!targetScreen) return 'none';
  if (targetScreen === 'external') return 'url';
  if (targetScreen === 'offers') return 'promo';
  return 'screen';
}

export function defaultScreenForModule(moduleKey: string) {
  return HOME_BANNER_SCREENS_BY_MODULE[moduleKey]?.[0] || '';
}

export function suggestBannerCode(moduleKey: string, name: string) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32) || 'banner';
  return `${moduleKey || 'app'}_${slug}`;
}
